import { Modal, useModals } from 'almanac-ui/Modal'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import cx from 'classnames'

import Styles from './TierUpgradeModal.module.scss'
import { PlanIcon } from 'components/Settings/TieredBilling/components/PlanIcon/PlanIcon'
import UserProfile1 from 'blaze-ui/icons/20/UserProfile1'
import { pluralize } from 'utils'
import { Approval, ArrowSkip, Brand, Hash2, Knowledge, LightningFilled, MessageText2 } from 'blaze-ui/icons'
import Iphone02 from 'blaze-ui/icons/16/Iphone02'
import { ArrowRightSquareContained } from 'blaze-ui/icons/20/ArrowRightSquareContained'
import { BlazeAI } from 'almanac-ui/icons'
import FileSearch1 from 'blaze-ui/icons/20/FileSearch1'
import PhoneCall01 from 'blaze-ui/icons/16/PhoneCall01'
import React, { useEffect, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import Bezier01 from 'blaze-ui/icons/16/Bezier01'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ManageSubscriptionModal } from 'components/ManageSubscriptionModal/ManageSubscriptionModal'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { capitalize } from 'lodash'
import { Text } from 'blaze-ui/Text/Text'

export interface Props {
  reason: 'limit_reached' | 'feature_disabled' | 'upgrade_plan'
  feature: string
  currentTier: PaymentPlan
  upgradeTier: PaymentPlan
  onUpgrade?: () => void
}

export const TierUpgradeModal = observer(({ reason, feature, upgradeTier, currentTier, onUpgrade }: Props) => {
  const { closeModal, openModal } = useModals()
  const [processing, setProcessing] = useState(false)
  const workspace = useCurrentWorkspace()
  const tierUsageStore = useRootStore().tierUsageStore
  const { currentSubscription, initialized, loading } = tierUsageStore
  const onTrial = currentSubscription?.trial_days_left

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  const handleUpgrade = async () => {
    if (!workspace) {
      showToastMessage(
        'Unable to upgrade your subscription. Please refresh your browser and try again, contact support if issue persists.',
        'error',
      )
      return
    }

    setProcessing(true)

    const res = await WorkspaceClient.updateSubscription(workspace?.id, upgradeTier.price.month.price_id)
    if (res.data.success) {
      showToastMessage(`You've upgraded to the ${upgradeTier.name} plan!`, 'success')
      closeModal()
      onUpgrade?.()
    } else {
      showToastMessage(
        'Unable to upgrade your subscription. Please refresh your browser and try again, contact support if issue persists.',
        'error',
      )
    }

    setProcessing(false)
  }

  return (
    <Modal.Root onClose={closeModal} onPressOutside={closeModal}>
      <Modal.Header
        className={Styles.headerText}
        title={
          onTrial
            ? `You're about to switch to the ${upgradeTier.name} Plan trial`
            : getHeaderText(reason, feature, currentTier.name)
        }
        onClose={closeModal}
      />
      <Modal.Content>
        <p className={Styles.bodyText}>To continue uninterrupted please consider updating to:</p>
        <div className={cx(Styles.bodyContainer, Styles[upgradeTier.key])}>
          <div className={Styles.bodyHeader}>
            <div className={Styles.bodyHeaderLeft}>
              <PlanIcon size={20} plan={upgradeTier.key} />
              <h4>{upgradeTier.name} Plan</h4>
            </div>
            <div className={Styles.bodyHeaderRight}>${upgradeTier.price.month.unit_amount}/month</div>
          </div>
          {upgradeTier.key === PaymentPlanKey.BlazeEnterprise ? (
            <div className={Styles.enterpriseText}>
              Our Enterprise Plan includes advanced security features, personalized training, and custom invoicing.
              <br />
              <br />
              Let’s schedule a call to discuss how we can tailor the solution to meet your specific needs!
            </div>
          ) : (
            <>
              <div className={Styles.subBodyContainer}>
                <div>
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.seats_limit}
                    icon={<UserProfile1 size={16} />}
                    feature={pluralize('Seat', parseInt(`${upgradeTier.base_price_meta.seats_limit}`) || 0)}
                  />
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.brand_kits_limit}
                    icon={<Brand size={16} />}
                    feature={'Brand Kits'}
                  />
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.integrations_limit}
                    icon={<Iphone02 size={16} />}
                    feature={'Accounts per '}
                  />
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.published_posts_limit}
                    icon={<LightningFilled size={16} />}
                    feature={'Published Posts per Platform'}
                  />
                  {extraLeftColumnFeatures[upgradeTier.key]?.map((feature) => (
                    <PlanFeatureRow key={feature.feature} {...feature} />
                  ))}
                </div>
                <div>
                  <PlanFeatureRow
                    limit={
                      upgradeTier.base_price_meta.ai_words_limit === 'unlimited'
                        ? 'Unlimited'
                        : `${parseInt(`${upgradeTier.base_price_meta.ai_words_limit}`) / 1000}K`
                    }
                    icon={<BlazeAI size={16} />}
                    feature={'AI Words'}
                  />
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.plagiarism_checks_limit}
                    icon={<FileSearch1 size={16} />}
                    feature={'Plagiarism Scans'}
                  />
                  <PlanFeatureRow
                    limit={upgradeTier.base_price_meta.source_materials_limit}
                    icon={<Knowledge size={16} />}
                    feature={'Source Materials'}
                  />
                  {extraRightColumnFeatures[upgradeTier.key]?.map((feature) => (
                    <PlanFeatureRow key={feature.feature} {...feature} />
                  ))}
                </div>
              </div>
              <div className={Styles.valueContainer}>
                <div className={Styles.valueContainerLeft}>
                  <h5>To get the same value, you'd need</h5>
                  {valueListItems[upgradeTier.key]}
                </div>
                <div className={Styles.valueContainerRight}>
                  Save money & simplify your workflow with Blaze.ai for only $39/mo billed yearly
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Content>
      {workspace?.subscription_provider !== 'apple' ? (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button
              variant="tertiary"
              onPress={() => {
                openModal(ManageSubscriptionModal, {})
              }}
            >
              See all plans
            </Button>
          </Modal.FooterContent>
          <Modal.FooterContent slot="right">
            {upgradeTier.key === PaymentPlanKey.BlazeEnterprise ? (
              <a href="mailto:support@blaze.ai?subject=Enterprise">
                <Button variant="primary">Book a call</Button>
              </a>
            ) : (
              <Button isDisabled={processing} variant="primary" onPress={handleUpgrade}>
                {processing ? 'Processing' : <>Upgrade to {upgradeTier.name}</>}
              </Button>
            )}
          </Modal.FooterContent>
        </Modal.Footer>
      ) : (
        <div className={Styles.appleFooterContent}>
          <Text>
            Your subscription is currently managed through the Blaze app on your iPhone or iPad. To manage your plan,
            please open the Blaze app on your mobile device.
          </Text>
        </div>
      )}
    </Modal.Root>
  )
})

const getHeaderText = (reason: Props['reason'], feature: string, planName: string) => {
  switch (reason) {
    case 'limit_reached':
      return `You’ve reached the limit for number of ${feature} on Blaze's ${planName} plan.`
    case 'feature_disabled':
      return `You do not have access to ${feature} on Blaze's ${planName} plan.`
    case 'upgrade_plan':
      return `Upgrade to have access to more features.`
    default:
      return 'Upgrade your plan to use this feature'
  }
}

interface PlanFeatureRowProps {
  limit?: number | string
  feature: string
  icon: React.ReactNode
}

const PlanFeatureRow = ({ icon, limit, feature }: PlanFeatureRowProps) => {
  return (
    <div className={Styles.rowContainer}>
      <div className={Styles.rowIconContainer}>{icon}</div>{' '}
      {!!limit && <b className={Styles.rowIconBold}>{typeof limit === 'number' ? limit : capitalize(limit)}</b>}{' '}
      {feature}
    </div>
  )
}

const valueListItems: Record<string, React.ReactNode> = {
  blaze_pro: (
    <>
      <ul className={Styles.valueList}>
        <li key="canva">
          <div>Canva Team</div>
          <div>$30</div>
        </li>
        <li key="chatgpt">
          <div>ChatGPT Plus</div>
          <div>$20</div>
        </li>
        <li key="hootsuite">
          <div>Hootsuite Professional</div>
          <div>$99</div>
        </li>
      </ul>
      <div className={Styles.horizontalRuler} />
      <h5>3 apps to manage and $149/mo</h5>
    </>
  ),
  blaze_team: (
    <>
      <ul className={Styles.valueList}>
        <li key="canva">
          <div>Canva Team</div>
          <div>$30</div>
        </li>
        <li key="chatgpt">
          <div>ChatGPT Team for 3</div>
          <div>$90</div>
        </li>
        <li key="hootsuite">
          <div>Hootsuite Team</div>
          <div>$250</div>
        </li>
      </ul>
      <div className={Styles.horizontalRuler} />
      <h5>3 apps to manage and $370/mo</h5>
    </>
  ),
  blaze_agency: (
    <>
      <ul className={Styles.valueList}>
        <li key="canva">
          <div>Canva Team</div>
          <div>$50</div>
        </li>
        <li key="chatgpt">
          <div>ChatGPT Team for 5</div>
          <div>$150</div>
        </li>
        <li key="hootsuite">
          <div>Hootsuite Team</div>
          <div>$250</div>
        </li>
      </ul>
      <div className={Styles.horizontalRuler} />
      <h5>3 apps to manage and $450/mo</h5>
    </>
  ),
}

const extraLeftColumnFeatures: Record<string, PlanFeatureRowProps[]> = {
  blaze_pro: [
    { icon: <ArrowRightSquareContained size={16} />, feature: 'Multiple Post Wizards' },
    { icon: <Hash2 size={16} />, feature: 'Link & Hashtag banks' },
  ],
  blaze_team: [{ icon: <Approval size={16} />, feature: 'Content Approval Tools' }],
  blaze_agency: [
    { icon: <Approval size={16} />, feature: 'Content Approval Tools' },
    { icon: <PhoneCall01 size={16} />, feature: 'Dedicated Onboarding' },
  ],
}

const extraRightColumnFeatures: Record<string, PlanFeatureRowProps[]> = {
  blaze_pro: [
    { icon: <PhoneCall01 size={16} />, feature: 'Priority Support' },
    { icon: <ArrowSkip size={16} />, feature: 'Priority AI Processing' },
  ],
  blaze_team: [{ icon: <MessageText2 size={16} />, feature: 'Comments & Tasks' }],
  blaze_agency: [
    { icon: <MessageText2 size={16} />, feature: 'Comments & Tasks' },
    { icon: <Bezier01 size={16} />, feature: 'Workspace volume discounts' },
  ],
}
